if (!customElements.get('dh-card-quick-add')) {
  class DhCardQuickAdd extends HTMLElement {
    constructor() {
      super();

      this.isBusy = false;
      this.lastTrigger = null;

      this.handleClickBound =
        this.handleClick.bind(this);

      this.resizeObserver = null;
    }

    connectedCallback() {
      if (
        this.dataset.initialized ===
        'true'
      ) {
        return;
      }

      this.dataset.initialized =
        'true';

      this.desktopTrigger =
        this.querySelector(
          '.dh-card-quick-add__trigger--desktop'
        );

      this.desktopPanel =
        this.querySelector(
          '.dh-card-quick-add__panel'
        );

      this.mobileTrigger =
        this.querySelector(
          '.dh-card-quick-add__mobile-trigger'
        );

      this.mobileTemplate =
        this.querySelector(
          '.dh-card-quick-add__mobile-template'
        );

      this.status =
        this.querySelector(
          '.dh-card-quick-add__status'
        );

      /*
       * Match DH overlay height exactly
       * to Dawn product image area.
       */
      this.syncImageHeight();

      const wrapper =
        this.closest(
          '.product-card-wrapper, .card-wrapper'
        );

      const imageArea =
        wrapper?.querySelector(
          '.card__inner'
        );

      if (
        imageArea &&
        'ResizeObserver' in window
      ) {
        this.resizeObserver =
          new ResizeObserver(() => {
            this.syncImageHeight();
          });

        this.resizeObserver.observe(
          imageArea
        );
      }

      /*
       * Capture click before Dawn's
       * stretched product link.
       */
      this.addEventListener(
        'click',
        this.handleClickBound,
        true
      );
    }

    disconnectedCallback() {
      this.removeEventListener(
        'click',
        this.handleClickBound,
        true
      );

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();

        this.resizeObserver = null;
      }
    }

    syncImageHeight() {
      const wrapper =
        this.closest(
          '.product-card-wrapper, .card-wrapper'
        );

      const imageArea =
        wrapper?.querySelector(
          '.card__inner'
        );

      if (!imageArea) return;

      const height =
        Math.ceil(
          imageArea.getBoundingClientRect()
            .height
        );

      if (height > 0) {
        this.style.height =
          `${height}px`;
      }
    }

    handleClick(event) {
      const trigger =
        event.target.closest(
          '.dh-card-quick-add__trigger--desktop, .dh-card-quick-add__mobile-trigger'
        );

      const desktopVariant =
        event.target.closest(
          '.dh-card-quick-add__variant'
        );

      const closeButton =
        event.target.closest(
          '.dh-card-quick-add__panel-close'
        );

      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (
          trigger.disabled ||
          this.isBusy
        ) {
          return;
        }

        this.lastTrigger =
          trigger;

        const variantId =
          trigger.dataset
            .dhVariantId;

        /*
         * Single variant:
         * add immediately.
         */
        if (variantId) {
          this.addVariant(
            variantId,
            trigger.dataset
              .dhQuantity || 1
          );

          return;
        }

        /*
         * Mobile.
         */
        if (
          trigger.classList.contains(
            'dh-card-quick-add__mobile-trigger'
          )
        ) {
          DhMobileQuickAdd.open(
            this
          );

          return;
        }

        /*
         * Desktop.
         */
        this.openDesktop();

        return;
      }

      if (desktopVariant) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (
          desktopVariant.disabled ||
          this.isBusy
        ) {
          return;
        }

        this.lastTrigger =
          desktopVariant;

        this.addVariant(
          desktopVariant.dataset
            .dhVariantId,

          desktopVariant.dataset
            .dhQuantity || 1
        );

        return;
      }

      if (closeButton) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.closeDesktop();
      }
    }

    openDesktop() {
      if (!this.desktopPanel) {
        return;
      }

      document
        .querySelectorAll(
          'dh-card-quick-add.is-open'
        )
        .forEach((element) => {
          if (element !== this) {
            element.closeDesktop?.(
              false
            );
          }
        });

      this.classList.add(
        'is-open'
      );

      this.desktopPanel.setAttribute(
        'aria-hidden',
        'false'
      );

      this.setStatus('');

      const first =
        this.desktopPanel.querySelector(
          '.dh-card-quick-add__variant:not(:disabled)'
        );

      if (first) {
        window.setTimeout(
          () => first.focus(),
          350
        );
      }
    }

    closeDesktop(
      restoreFocus = true
    ) {
      if (!this.desktopPanel) {
        return;
      }

      this.classList.remove(
        'is-open'
      );

      this.desktopPanel.setAttribute(
        'aria-hidden',
        'true'
      );

      this.setStatus('');

      if (
        restoreFocus &&
        this.desktopTrigger
      ) {
        this.desktopTrigger.focus();
      }
    }

    setStatus(message) {
      if (this.status) {
        this.status.textContent =
          message || '';
      }

      if (
        DhMobileQuickAdd.activeCard ===
        this
      ) {
        DhMobileQuickAdd.setStatus(
          message
        );
      }
    }

    setBusy(state) {
      this.isBusy = state;

      this.classList.toggle(
        'is-loading',
        state
      );

      /*
       * Do NOT disable all size buttons.
       * Otherwise CSS thinks every size
       * is sold out.
       */
      this.querySelectorAll(
        '[data-dh-variant-id]'
      ).forEach((button) => {
        button.setAttribute(
          'aria-busy',
          state ? 'true' : 'false'
        );
      });

      if (
        DhMobileQuickAdd.activeCard ===
        this
      ) {
        DhMobileQuickAdd.setBusy(
          state
        );
      }
    }

    async addVariant(
      variantId,
      quantity = 1
    ) {
      if (
        !variantId ||
        this.isBusy
      ) {
        return;
      }

      const qty =
        Math.max(
          1,
          parseInt(
            quantity,
            10
          ) || 1
        );

      this.setStatus('');

      this.setBusy(true);

      const cart =
        document.querySelector(
          'cart-notification'
        ) ||
        document.querySelector(
          'cart-drawer'
        );

      try {
        const config =
          typeof fetchConfig ===
          'function'
            ? fetchConfig(
                'javascript'
              )
            : {
                method:
                  'POST',

                headers: {
                  Accept:
                    'application/json',
                },
              };

        config.headers =
          config.headers || {};

        config.headers[
          'X-Requested-With'
        ] =
          'XMLHttpRequest';

        delete config.headers[
          'Content-Type'
        ];

        const formData =
          new FormData();

        formData.append(
          'id',
          variantId
        );

        formData.append(
          'quantity',
          qty
        );

        /*
         * Ask Dawn for cart drawer
         * sections in same response.
         */
        if (cart) {
          const sections =
            cart.getSectionsToRender?.();

          if (
            sections?.length
          ) {
            formData.append(
              'sections',
              sections
                .map(
                  (section) =>
                    section.id
                )
                .join(',')
            );

            formData.append(
              'sections_url',
              window.location.pathname
            );
          }

          cart.setActiveElement?.(
            this.lastTrigger
          );
        }

        config.body =
          formData;

        const response =
          await fetch(
            window.routes
              ?.cart_add_url ||
              '/cart/add.js',
            config
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          data.status
        ) {
          throw new Error(
            data.description ||
              data.message ||
              'Unable to add this item.'
          );
        }

        /*
         * Dawn cart event.
         */
        if (
          typeof publish ===
            'function' &&
          typeof PUB_SUB_EVENTS !==
            'undefined'
        ) {
          try {
            publish(
              PUB_SUB_EVENTS
                .cartUpdate,
              {
                source:
                  'dh-card-quick-add',

                productVariantId:
                  variantId,

                cartData:
                  data,
              }
            );
          } catch (error) {
            console.warn(
              'DH cart event:',
              error
            );
          }
        }

        this.closeDesktop(
          false
        );

        if (
          DhMobileQuickAdd.activeCard ===
          this
        ) {
          DhMobileQuickAdd.close(
            false
          );
        }

        /*
         * Dawn cart drawer.
         */
        if (
          cart &&
          typeof cart.renderContents ===
            'function'
        ) {
          cart.classList.remove(
            'is-empty'
          );

          cart.renderContents(
            data
          );
        } else {
          window.location.href =
            window.routes
              ?.cart_url ||
            '/cart';
        }
      } catch (error) {
        console.error(
          'DH Quick Add:',
          error
        );

        this.setStatus(
          error.message ||
            'Unable to add this item.'
        );
      } finally {
        this.setBusy(false);
      }
    }
  }

  customElements.define(
    'dh-card-quick-add',
    DhCardQuickAdd
  );
}

/* =========================================================
   MOBILE BOTTOM SHEET
========================================================= */

const DhMobileQuickAdd = {
  element: null,

  content: null,

  status: null,

  activeCard: null,

  previousFocus: null,

  init() {
    if (this.element) {
      return;
    }

    const wrapper =
      document.createElement(
        'div'
      );

    wrapper.className =
      'dh-mobile-quick-add';

    wrapper.setAttribute(
      'aria-hidden',
      'true'
    );

    wrapper.innerHTML = `
      <div
        class="dh-mobile-quick-add__overlay"
        data-dh-mobile-close
      ></div>

      <div
        class="dh-mobile-quick-add__sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Quick add"
        tabindex="-1"
      >

        <div
          class="dh-mobile-quick-add__handle"
          aria-hidden="true"
        ></div>

        <button
          type="button"
          class="dh-mobile-quick-add__close"
          data-dh-mobile-close
          aria-label="Close"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M6 6l12 12M18 6L6 18"
            ></path>
          </svg>
        </button>

        <div
          class="dh-mobile-quick-add__content"
        ></div>

        <div
          class="dh-mobile-quick-add__status"
          role="status"
          aria-live="polite"
        ></div>

      </div>
    `;

    document.body.appendChild(
      wrapper
    );

    this.element =
      wrapper;

    this.content =
      wrapper.querySelector(
        '.dh-mobile-quick-add__content'
      );

    this.status =
      wrapper.querySelector(
        '.dh-mobile-quick-add__status'
      );

    wrapper.addEventListener(
      'click',
      (event) => {
        const close =
          event.target.closest(
            '[data-dh-mobile-close]'
          );

        if (close) {
          event.preventDefault();
          event.stopPropagation();

          this.close();

          return;
        }

        const variant =
          event.target.closest(
            '.dh-mobile-quick-add__variant'
          );

        if (
          !variant ||
          !this.activeCard
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (
          variant.disabled ||
          this.activeCard
            .isBusy
        ) {
          return;
        }

        this.activeCard
          .lastTrigger =
          variant;

        this.activeCard
          .addVariant(
            variant.dataset
              .dhVariantId,

            variant.dataset
              .dhQuantity ||
              1
          );
      },
      true
    );
  },

  open(card) {
    this.init();

    if (
      !card ||
      !card.mobileTemplate
    ) {
      return;
    }

    this.activeCard =
      card;

    this.previousFocus =
      document.activeElement;

    this.content.innerHTML =
      '';

    this.content.appendChild(
      card.mobileTemplate
        .content
        .cloneNode(true)
    );

    this.setStatus('');

    this.element.classList.add(
      'is-open'
    );

    this.element.setAttribute(
      'aria-hidden',
      'false'
    );

    document.body.classList.add(
      'dh-quick-add-open'
    );

    window.setTimeout(
      () => {
        const first =
          this.content.querySelector(
            '.dh-mobile-quick-add__variant:not(:disabled)'
          );

        first?.focus();
      },
      350
    );
  },

  close(
    restoreFocus = true
  ) {
    if (!this.element) {
      return;
    }

    this.element.classList.remove(
      'is-open'
    );

    this.element.setAttribute(
      'aria-hidden',
      'true'
    );

    document.body.classList.remove(
      'dh-quick-add-open'
    );

    this.setStatus('');

    const previous =
      this.previousFocus;

    this.activeCard = null;
    this.previousFocus = null;

    if (
      restoreFocus &&
      previous
    ) {
      window.setTimeout(
        () => {
          previous.focus();
        },
        50
      );
    }
  },

  setStatus(message) {
    if (this.status) {
      this.status.textContent =
        message || '';
    }
  },

  setBusy(state) {
    if (!this.element) {
      return;
    }

    this.element
      .querySelectorAll(
        '[data-dh-variant-id]'
      )
      .forEach(
        (button) => {
          button.setAttribute(
            'aria-busy',
            state
              ? 'true'
              : 'false'
          );
        }
      );
  },
};

/* =========================================================
   ESC + OUTSIDE CLICK
========================================================= */

if (
  !window.dhQuickAddEvents
) {
  window.dhQuickAddEvents =
    true;

  document.addEventListener(
    'keydown',
    (event) => {
      if (
        event.key !==
        'Escape'
      ) {
        return;
      }

      if (
        DhMobileQuickAdd
          .element
          ?.classList.contains(
            'is-open'
          )
      ) {
        DhMobileQuickAdd.close();

        return;
      }

      document
        .querySelector(
          'dh-card-quick-add.is-open'
        )
        ?.closeDesktop();
    }
  );

  document.addEventListener(
    'click',
    (event) => {
      const openCard =
        document.querySelector(
          'dh-card-quick-add.is-open'
        );

      if (
        openCard &&
        !event.target.closest(
          'dh-card-quick-add'
        )
      ) {
        openCard.closeDesktop(
          false
        );
      }
    }
  );
}